import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchHangarUnits,
  fetchAllUnitsForAccount,
  fetchHangarUnit,
  createHangarUnit,
  createHangarUnits,
  updateHangarUnit,
  deleteHangarUnit,
  deleteAllHangarUnits,
} from '@/lib/api/hangar-units'
import type { HangarUnitInsert, HangarUnitUpdate } from '@/types/hangar-units'
import { hangarKeys } from './useHangars'
import { useTenant } from '@/contexts'
import { toast } from 'sonner'

/**
 * Query key factory for hangar units
 * Provides consistent keys for cache management
 */
export const hangarUnitKeys = {
  all: ['hangarUnits'] as const,
  lists: () => [...hangarUnitKeys.all, 'list'] as const,
  list: (hangarId: string) => [...hangarUnitKeys.lists(), hangarId] as const,
  details: () => [...hangarUnitKeys.all, 'detail'] as const,
  detail: (id: string) => [...hangarUnitKeys.details(), id] as const,
}

/**
 * Fetch all units for a hangar
 */
export function useHangarUnits(hangarId: string | undefined) {
  return useQuery({
    queryKey: hangarUnitKeys.list(hangarId || ''),
    queryFn: () => fetchHangarUnits(hangarId!),
    enabled: !!hangarId,
  })
}

/**
 * Fetch all units for the current organization (across all buildings)
 */
export function useAllUnits() {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: [...hangarUnitKeys.all, 'all', organizationId],
    queryFn: () => fetchAllUnitsForAccount(organizationId!),
    enabled: !!organizationId,
  })
}

/**
 * Fetch a single hangar unit by ID
 */
export function useHangarUnit(id: string | undefined) {
  return useQuery({
    queryKey: hangarUnitKeys.detail(id || ''),
    queryFn: () => fetchHangarUnit(id!),
    enabled: !!id,
  })
}

/**
 * Create a new hangar unit
 */
export function useCreateHangarUnit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: HangarUnitInsert) => createHangarUnit(data),
    onSuccess: (_, variables) => {
      // Invalidate list for this building
      queryClient.invalidateQueries({
        queryKey: hangarUnitKeys.list(variables.building_id),
      })
      toast.success('Unit created successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to create unit', {
        description: error.message,
      })
    },
  })
}

/**
 * Create multiple hangar units at once (batch)
 * Used by T-hangar configurator to generate units from template
 */
export function useCreateHangarUnits() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (units: HangarUnitInsert[]) => createHangarUnits(units),
    onSuccess: (_, variables) => {
      // Invalidate list for this building (all units have same building_id)
      if (variables.length > 0) {
        queryClient.invalidateQueries({
          queryKey: hangarUnitKeys.list(variables[0].building_id),
        })
      }
      toast.success(`${variables.length} units created successfully`)
    },
    onError: (error: Error) => {
      toast.error('Failed to create units', {
        description: error.message,
      })
    },
  })
}

/**
 * Update an existing hangar unit
 */
export function useUpdateHangarUnit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      hangarId: string
      data: HangarUnitUpdate
    }) => updateHangarUnit(id, data),
    onSuccess: (_, { id, hangarId }) => {
      // Invalidate both list and detail queries
      queryClient.invalidateQueries({ queryKey: hangarUnitKeys.list(hangarId) })
      queryClient.invalidateQueries({ queryKey: hangarUnitKeys.detail(id) })
      toast.success('Unit updated successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to update unit', {
        description: error.message,
      })
    },
  })
}

/**
 * Delete a hangar unit (soft delete)
 */
export function useDeleteHangarUnit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id }: { id: string; hangarId: string }) =>
      deleteHangarUnit(id),
    onSuccess: (_, { hangarId }) => {
      // Invalidate list for this hangar
      queryClient.invalidateQueries({ queryKey: hangarUnitKeys.list(hangarId) })
      toast.success('Unit deleted successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to delete unit', {
        description: error.message,
      })
    },
  })
}

/**
 * Delete all units for a hangar (soft delete)
 * Used when regenerating units from template
 */
export function useDeleteAllHangarUnits() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (hangarId: string) => deleteAllHangarUnits(hangarId),
    onSuccess: (_, hangarId) => {
      // Invalidate list for this hangar
      queryClient.invalidateQueries({ queryKey: hangarUnitKeys.list(hangarId) })
      // Also invalidate hangar detail (may show unit count)
      queryClient.invalidateQueries({ queryKey: hangarKeys.lists() })
      toast.success('All units deleted successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to delete units', {
        description: error.message,
      })
    },
  })
}
